import { useGlobalValue, useSetGlobalState } from '@src/hooks/useGlobalState';
import { globalKeys } from '../queryKeyFactory';
import { Snackbar } from '@mui/material';
import { IconToastFail, IconToastSuccess } from '@resources/icon';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Color } from '@src/common/styles/color';
import { IToastProps, IToastStyle } from '@src/common/config/ICode';

const ToastStyle = styled.div<IToastStyle>`
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: 2px;
  font-size: 14px;
  color: #fafafb;
  background: linear-gradient(270deg, #aa51e0 0%, #9b51e0 100%);
  padding: 13px 16px;
  > span {
    margin-left: 16px;
  }

  ${({ severity }) => {
    if (severity === 'error')
      return css`
        background: ${Color.error};
      `;
  }}
`;

/**
 * dateRangePicker hook
 * @type {(props?: IRangeCalendarState) => {dateRange: {from: Date, to: Date }; openHandle: () => void; closeHandle: () => void; component: () => JSX.Element}}
 */
export const useToast = () => {
  //#region global state
  const toastData = useGlobalValue(globalKeys.toast, {
    open: false,
    text: '',
    severity: 'success'
  });
  const toastFetcher = useSetGlobalState([...globalKeys.toast]);
  const { open, text, severity } = toastData;
  //#endregion

  //#region handler
  const openHandle = (data: IToastProps) => {
    toastFetcher({
      ...toastData,
      open: true,
      ...data
    });
  };
  const closeHandle = () => {
    toastFetcher({ ...toastData, open: false });
  };
  //#endregion

  //#region component
  const Component = (): JSX.Element =>
    open && (
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={closeHandle}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <ToastStyle severity={severity}>
          {severity === 'success' ? (
            <IconToastSuccess width={20} height={20} />
          ) : (
            <IconToastFail width={20} height={20} />
          )}
          <span> {text}</span>
        </ToastStyle>
      </Snackbar>
    );

  return {
    openHandle,
    Component
  };
  //#endregion
};
